import { IAPIRoutes } from "../../../../config";
import type {
  INeoActionExecutionRepository,
  NeoActionExecution,
  NeoActionExecutionListOptions,
  NeoActionExecutionListResponse,
} from "../../../core/ports/driven/neoActionExecutionRepository";

const fillRoute = (
  template: string,
  params: Record<string, string | number>
): string => {
  let out = template;
  for (const [key, value] of Object.entries(params)) {
    out = out.replace(`{${key}}`, String(value));
  }
  return out;
};

export type INeoActionExecutionRepositoryFactory = ReturnType<
  typeof NeoActionExecutionRepositoryFactory
>;

export const NeoActionExecutionRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}): INeoActionExecutionRepository => {
  const { GET, POST } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  const authHeader = async () => ({
    Authorization: `Bearer ${await getAuthenticationToken()}`,
  });

  const listActionExecutionsAPI = async (
    projectId: number,
    opts?: NeoActionExecutionListOptions
  ): Promise<NeoActionExecutionListResponse> => {
    const params: Record<string, string | number> = {
      project_id: projectId,
      limit: opts?.limit ?? 200,
    };
    if (opts?.offset) params.offset = opts.offset;
    if (opts?.status) params.status = opts.status;
    if (opts?.conversationId) params.conversation_id = opts.conversationId;
    return GET(APIRoutes.apiNeoActionExecutions, params, await authHeader());
  };

  const approveActionExecutionAPI = async (
    executionId: number
  ): Promise<NeoActionExecution> =>
    POST(
      fillRoute(APIRoutes.apiNeoActionExecutionApprove, {
        execution_id: executionId,
      }),
      {},
      await authHeader()
    );

  const rejectActionExecutionAPI = async (
    executionId: number,
    reason?: string
  ): Promise<NeoActionExecution> =>
    POST(
      fillRoute(APIRoutes.apiNeoActionExecutionReject, {
        execution_id: executionId,
      }),
      reason ? { reason } : {},
      await authHeader()
    );

  const undoActionExecutionAPI = async (
    executionId: number
  ): Promise<NeoActionExecution> =>
    POST(
      fillRoute(APIRoutes.apiNeoActionExecutionUndo, {
        execution_id: executionId,
      }),
      {},
      await authHeader()
    );

  return {
    listActionExecutionsAPI,
    approveActionExecutionAPI,
    rejectActionExecutionAPI,
    undoActionExecutionAPI,
  };
};
